/**
 * CARIPanel — the Comprehensive Aquifer Reasoning Intelligence chat surface.
 *
 * Sister component to TerraPanel. Reuses TERRA's visual primitives
 * (MarkdownMessage, ToolCallCard, InlineDataCard, CitationBadge,
 * StreamingCursor) so the look-and-feel is consistent across both agents
 * but the branding, color palette, and data wiring point at CARI.
 *
 * Features shipped in this version:
 *   - Streaming SSE text with delta batching (50ms)
 *   - Streaming cursor while assistant is typing
 *   - Tool-call cards with running spinner + completed summary
 *   - Inline generative-UI components (tables, charts, metrics)
 *   - Per-message thumbs up/down with feedback comment input
 *   - Voice-to-text (Web Speech API)
 *   - Conversation history sidebar (list, switch, rename, delete)
 *   - Suggestion chips (capability-profile-aware)
 *   - Cancel-in-flight stream button
 *   - Auto-scroll on new content
 *   - Capability profile badge in the header
 *
 * Premium features queued for follow-up (parity with TERRA):
 *   - @mention autocomplete (component reuse + new endpoint)
 *   - File attachment with site selector + persistence toggle
 *   - Inline citation [N] badges in markdown
 *   - Conversation search
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import {
  ArrowPathIcon,
  BeakerIcon,
  MicrophoneIcon,
  PaperAirplaneIcon,
  StopIcon,
  TrashIcon,
  XMarkIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { useMatch } from 'react-router-dom';
import { useCARIStore } from '@/store/cari';
import { submitFeedback } from '@/api/cari';
import { isVoiceSupported, startListening } from '@/lib/voiceInput';

// Reused TERRA primitives — agent-agnostic. If they ever drift, lift to a
// shared frontend/src/components/agent-runtime/ directory.
import { MarkdownMessage } from '@/components/terra/MarkdownMessage';
import { ToolCallCard } from '@/components/terra/ToolCallCard';
import { InlineDataCard } from '@/components/terra/InlineDataCard';
import { StreamingCursor } from '@/components/terra/StreamingCursor';

// CARI-specific @mention autocomplete (Phreatic entity types).
import {
  CARIMentionAutocomplete,
  useCARIMentionState,
  type CARIMentionHandle,
} from '@/components/cari/MentionAutocomplete';


// Profile label badge removed 2026-06 — CARI is now unified (no per-user
// gating). Header just shows the agent name.


// ── Per-message thumbs up/down with inline feedback box ───────────────

function FeedbackButtons({
  messageId,
  initialRating,
}: {
  messageId: string;
  initialRating: number | null;
}) {
  const [rating, setRating] = useState<number | null>(initialRating);
  const [showInput, setShowInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const onRate = useCallback(
    async (value: 1 | -1) => {
      const next = rating === value ? null : value;
      setRating(next);
      if (next === -1) setShowInput(true);
      else { setShowInput(false); setFeedbackText(''); }
      if (next) {
        try {
          await submitFeedback(messageId, next);
          setError(null);
        } catch (e) {
          console.error('CARI feedback rating failed', e);
          setRating(rating); // roll back optimistic
          setError("Couldn't save feedback");
        }
      }
    },
    [messageId, rating],
  );

  const onSendComment = useCallback(async () => {
    if (!feedbackText.trim()) {
      setShowInput(false);
      return;
    }
    try {
      await submitFeedback(messageId, -1, feedbackText.trim());
      setShowInput(false);
      setError(null);
    } catch (e) {
      console.error('CARI feedback comment failed', e);
      setError("Couldn't send comment");
    }
  }, [messageId, feedbackText]);

  return (
    <div className="relative flex items-center gap-1">
      <button
        onClick={() => onRate(1)}
        className={`p-1 rounded transition-colors ${
          rating === 1
            ? 'text-emerald-400 bg-emerald-500/20'
            : 'text-mono-400 hover:text-mono-200 hover:bg-mono-700/50'
        }`}
        title="Good response"
      >
        <HandThumbUpIcon className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => onRate(-1)}
        className={`p-1 rounded transition-colors ${
          rating === -1
            ? 'text-red-400 bg-red-500/20'
            : 'text-mono-400 hover:text-mono-200 hover:bg-mono-700/50'
        }`}
        title="Bad response"
      >
        <HandThumbDownIcon className="w-3.5 h-3.5" />
      </button>
      {showInput && (
        <div className="absolute -bottom-12 right-0 flex gap-1 bg-mono-800 border border-mono-700/50 rounded-md p-1 shadow-lg">
          <input
            type="text"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSendComment()}
            placeholder="What went wrong?"
            className="text-[10px] bg-transparent border-none text-mono-200 placeholder-mono-500 focus:outline-none w-40 px-1"
            autoFocus
          />
          <button onClick={onSendComment} className="text-[10px] text-cyan-300 hover:text-cyan-200 px-1">
            Send
          </button>
        </div>
      )}
      {error && (
        <span
          role="alert"
          className="absolute -bottom-5 right-0 text-[10px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-1.5 py-0.5 whitespace-nowrap"
        >
          {error}
        </span>
      )}
    </div>
  );
}


// ── Panel ─────────────────────────────────────────────────────────────

export function CARIPanel() {
  const {
    isOpen,
    close,
    isPinned,
    pin,
    unpin,
    conversations,
    activeConversationId,
    messages,
    isStreaming,
    streamingText,
    activeToolCalls,
    pendingComponents,
    citations,
    suggestions,
    rateLimitUntil,
    fetchConversations,
    newConversation,
    switchConversation,
    removeConversation,
    renameConversationLocal,
    sendMessage,
    cancelStream,
  } = useCARIStore();

  const [input, setInput] = useState('');
  const [cursorPos, setCursorPos] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [editingConvId, setEditingConvId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const mentionHandleRef = useRef<CARIMentionHandle | null>(null);

  // Current site from the URL (when the panel is open on a Phreatic site
  // page). Passed to the orchestrator as `context.site_id` so CARI knows
  // what "the current site" refers to without the user having to @mention it.
  const siteMatch = useMatch('/phreatic/sites/:siteId/*');
  const currentSiteId = siteMatch?.params.siteId ?? null;

  const buildSendContext = useCallback(() => {
    if (!currentSiteId) return undefined;
    return { site_id: currentSiteId } as Record<string, unknown>;
  }, [currentSiteId]);

  // @mention state derived from the current textarea value + cursor.
  const mentionState = useCARIMentionState(input, cursorPos);

  // When the user selects a mention from the dropdown, splice it into the
  // input replacing the in-flight `@query` token.
  const handleMentionSelect = useCallback(
    (item: { name: string }) => {
      if (mentionState.atIndex < 0) return;
      const before = input.slice(0, mentionState.atIndex);
      const after = input.slice(cursorPos);
      const next = `${before}@${item.name} ${after}`;
      const nextCursor = before.length + item.name.length + 2;
      setInput(next);
      setCursorPos(nextCursor);
      // Restore the DOM caret after React flushes the new value — without
      // this the browser leaves the caret where it was before the splice,
      // which lands inside the inserted mention.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextCursor, nextCursor);
      });
    },
    [input, cursorPos, mentionState.atIndex],
  );

  // ── Auto-scroll to bottom when new content lands ────────────────
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingText, activeToolCalls, pendingComponents]);

  // ── Fetch conversations on open ─────────────────────────────────
  useEffect(() => {
    if (isOpen && conversations.length === 0) {
      fetchConversations().catch(() => {});
    }
  }, [isOpen, conversations.length, fetchConversations]);

  // ── Rate-limit countdown ─────────────────────────────────────────
  useEffect(() => {
    if (!rateLimitUntil) {
      setRateLimitCountdown(0);
      return;
    }
    const update = () => {
      const secs = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000));
      setRateLimitCountdown(secs);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [rateLimitUntil]);

  const handleSend = useCallback(
    async (text?: string) => {
      const trimmed = (text || input).trim();
      if (!trimmed || isStreaming) return;
      if (!text) setInput('');

      if (!activeConversationId) {
        try {
          await newConversation(currentSiteId ?? undefined);
        } catch {
          return;
        }
      }
      sendMessage(trimmed, buildSendContext());
    },
    [
      input,
      isStreaming,
      activeConversationId,
      newConversation,
      sendMessage,
      currentSiteId,
      buildSendContext,
    ],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // If the mention dropdown is open and consumes the key (arrow nav,
      // Enter to select, Escape to close), don't fall through to send.
      if (mentionState.active && mentionHandleRef.current) {
        if (mentionHandleRef.current.handleKeyDown(e)) return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, mentionState.active],
  );

  const toggleVoice = useCallback(() => {
    if (isRecording) {
      stopRecordingRef.current?.();
      stopRecordingRef.current = null;
      setIsRecording(false);
      return;
    }
    setIsRecording(true);
    stopRecordingRef.current = startListening(
      (transcript, isFinal) => {
        setInput(transcript);
        if (isFinal) {
          stopRecordingRef.current = null;
          setIsRecording(false);
          if (transcript.trim()) handleSend(transcript);
        }
      },
      (err) => {
        console.error('CARI voice input error', err);
        setIsRecording(false);
        stopRecordingRef.current = null;
      },
    );
  }, [isRecording, handleSend]);

  const onSuggestion = useCallback(
    (text: string) => {
      setInput('');
      const ctx = buildSendContext();
      if (!activeConversationId) {
        newConversation(currentSiteId ?? undefined).then(() =>
          sendMessage(text, ctx),
        );
      } else {
        sendMessage(text, ctx);
      }
    },
    [
      activeConversationId,
      newConversation,
      sendMessage,
      currentSiteId,
      buildSendContext,
    ],
  );

  // Memoize the inline-component list per message position so we can
  // render the pending components after the last assistant message
  // without re-iterating on every keystroke.
  const componentsAfterLastAssistant = useMemo(() => pendingComponents, [pendingComponents]);

  if (!isOpen) return null;

  return (
    <div
      className={
        'fixed right-4 top-20 bottom-4 z-[1100] flex w-[440px] flex-col ' +
        'overflow-hidden rounded-xl border border-cyan-500/30 bg-mono-950/95 ' +
        'shadow-2xl shadow-cyan-500/10 backdrop-blur-md'
      }
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-mono-800/60 bg-gradient-to-r from-cyan-900/30 to-teal-900/30 px-4 py-3">
        <div className="flex items-center gap-2">
          <BeakerIcon className="h-5 w-5 text-cyan-300" />
          <span className="text-sm font-semibold tracking-wide text-mono-100">CARI</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="rounded p-1 text-mono-400 hover:bg-mono-800 hover:text-mono-200"
            title="Conversation history"
          >
            <ListBulletIcon className="h-4 w-4" />
          </button>
          <button
            onClick={isPinned ? unpin : pin}
            className={`rounded p-1 hover:bg-mono-800 ${
              isPinned ? 'text-cyan-300' : 'text-mono-400 hover:text-mono-200'
            }`}
            title={isPinned ? 'Unpin panel' : 'Pin panel'}
          >
            <BeakerIcon className="h-4 w-4" />
          </button>
          <button
            onClick={close}
            className="rounded p-1 text-mono-400 hover:bg-mono-800 hover:text-mono-200"
            title="Close panel"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Conversation history (collapsible) */}
      {showHistory && (
        <div className="max-h-64 overflow-y-auto border-b border-mono-800/60 bg-mono-900/40 px-2 py-2">
          <div className="mb-2 flex items-center justify-between px-2">
            <span className="text-xs uppercase tracking-wide text-mono-400">History</span>
            <button
              onClick={async () => {
                await newConversation();
                setShowHistory(false);
              }}
              className="text-[10px] text-cyan-300 hover:text-cyan-200"
            >
              + New
            </button>
          </div>
          {conversations.length === 0 && (
            <div className="px-2 py-3 text-center text-[11px] text-mono-500">No conversations yet</div>
          )}
          {conversations.map((c) => (
            <div
              key={c.id}
              className={`group flex items-center gap-1 rounded px-2 py-1.5 text-xs ${
                c.id === activeConversationId
                  ? 'bg-cyan-500/10 text-cyan-100'
                  : 'text-mono-300 hover:bg-mono-800/60'
              }`}
            >
              {editingConvId === c.id ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      await renameConversationLocal(c.id, editTitle);
                      setEditingConvId(null);
                    } else if (e.key === 'Escape') {
                      setEditingConvId(null);
                    }
                  }}
                  className="flex-1 bg-transparent text-xs text-mono-100 focus:outline-none"
                  autoFocus
                />
              ) : (
                <button
                  onClick={() => {
                    switchConversation(c.id);
                    setShowHistory(false);
                  }}
                  onDoubleClick={() => {
                    setEditingConvId(c.id);
                    setEditTitle(c.title);
                  }}
                  className="flex-1 truncate text-left"
                  title="Click to switch; double-click to rename"
                >
                  {c.title}
                </button>
              )}
              <button
                onClick={() => removeConversation(c.id)}
                className="opacity-0 group-hover:opacity-100 text-mono-500 hover:text-red-400"
                title="Delete"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !isStreaming && (
          <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4 text-sm text-mono-200">
            <p className="mb-2 font-medium text-cyan-200">
              I'm CARI. Hydrogeology specialist for Phreatic — pumps, telemetry, drawdown,
              supply planning, and compliance.
            </p>
            <p className="text-xs text-mono-400">
              Ask me about a site, a wellfield, an alert, or a regulatory deadline.
              I'll show my work and tell you what to verify before acting.
            </p>
          </div>
        )}

        {messages.map((m) => {
          if (m.role === 'user') {
            return (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[85%] rounded-xl bg-cyan-500/15 border border-cyan-500/30 px-3 py-2 text-sm text-mono-100">
                  {m.content}
                </div>
              </div>
            );
          }
          if (m.role === 'assistant') {
            return (
              <div key={m.id} className="group">
                <MarkdownMessage content={m.content || ''} citations={citations} />
                <div className="mt-1 flex justify-end opacity-0 transition-opacity group-hover:opacity-100">
                  <FeedbackButtons messageId={m.id} initialRating={m.rating} />
                </div>
              </div>
            );
          }
          if (m.role === 'tool_call') {
            return (
              <ToolCallCard
                key={m.id}
                toolCall={{
                  name: m.tool_name || '',
                  input: (m.tool_input as Record<string, unknown>) || {},
                  isActive: false,
                  summary: undefined,
                }}
              />
            );
          }
          return null;
        })}

        {/* In-flight tool calls (running spinners) */}
        {activeToolCalls
          .filter((tc) => tc.isActive)
          .map((tc, i) => (
            <ToolCallCard key={`active-${i}`} toolCall={tc} />
          ))}

        {/* Streaming assistant text */}
        {streamingText && (
          <div>
            <MarkdownMessage content={streamingText} citations={citations} />
            <StreamingCursor />
          </div>
        )}

        {/* Inline generative-UI components from this turn */}
        {componentsAfterLastAssistant.map((c, i) => (
          <InlineDataCard key={`comp-${i}`} component={c} />
        ))}
      </div>

      {/* Suggestion chips */}
      {!isStreaming && messages.length === 0 && (
        <div className="border-t border-mono-800/60 px-3 py-2">
          <div className="flex flex-wrap gap-1.5">
            {(suggestions ?? []).map((s) => (
              <button
                key={s}
                onClick={() => onSuggestion(s)}
                className="rounded-full border border-mono-700/60 bg-mono-800/40 px-2.5 py-1 text-[11px] text-mono-300 hover:border-cyan-500/40 hover:text-cyan-200"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-mono-800/60 px-3 py-3">
        <div className="relative flex items-end gap-2">
          {/* @mention autocomplete dropdown — positioned via absolute
              inside this relative container, anchored to the top of the
              textarea. */}
          <div className="absolute inset-x-0 bottom-full">
            <CARIMentionAutocomplete
              ref={mentionHandleRef}
              query={mentionState.query}
              visible={mentionState.active}
              onSelect={handleMentionSelect}
              onClose={() => setCursorPos(0)}
            />
          </div>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setCursorPos(e.target.selectionStart ?? 0);
            }}
            onSelect={(e) =>
              setCursorPos((e.target as HTMLTextAreaElement).selectionStart ?? 0)
            }
            onKeyDown={handleKeyDown}
            placeholder={
              rateLimitCountdown > 0
                ? `Rate-limited. Try again in ${rateLimitCountdown}s.`
                : 'Ask CARI… (type @ to mention a site, well, or aquifer)'
            }
            rows={2}
            disabled={isStreaming || rateLimitCountdown > 0}
            className="min-h-[3.5rem] max-h-32 flex-1 resize-none rounded-lg border border-mono-700/50 bg-mono-900/60 px-3 py-2 text-sm leading-snug text-mono-100 placeholder-mono-500 focus:border-cyan-500/40 focus:outline-none focus:ring-1 focus:ring-cyan-500/20"
            aria-label="Message CARI"
          />
          {isStreaming ? (
            <button
              onClick={cancelStream}
              className="flex-shrink-0 rounded-lg border border-red-500/30 bg-red-500/20 p-2 text-red-400 transition-colors hover:bg-red-500/30"
              title="Stop generating"
            >
              <StopIcon className="h-4 w-4" />
            </button>
          ) : (
            <>
              {isVoiceSupported() && (
                <button
                  onClick={toggleVoice}
                  className={`flex-shrink-0 rounded-lg border p-2 transition-colors ${
                    isRecording
                      ? 'animate-pulse border-red-500/30 bg-red-500/20 text-red-400'
                      : 'border-mono-700/50 bg-mono-800/40 text-mono-400 hover:border-cyan-500/30 hover:text-cyan-400'
                  }`}
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  <MicrophoneIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || rateLimitCountdown > 0}
                className="flex-shrink-0 rounded-lg border border-cyan-500/30 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 p-2 text-cyan-300 transition-colors hover:from-cyan-500/30 hover:to-teal-500/30 disabled:opacity-40"
                title="Send"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
        {rateLimitCountdown > 0 && (
          <div className="mt-1 flex items-center gap-1 text-[10px] text-amber-400">
            <ArrowPathIcon className="h-3 w-3 animate-spin" />
            Rate-limited. Try again in {rateLimitCountdown}s.
          </div>
        )}
      </div>
    </div>
  );
}

export default CARIPanel;
